import { FC, useEffect, useRef, useState } from "react";
import { appInsights, reactPlugin, withAITracking } from "../../../appInsights";
import { Col, Card, Table, Row, Tabs, DatePicker, Spin, Button, Input, Space, Tooltip, Collapse } from "antd";
import "./medical-history.scss";
import BarChart from "./bar-chart";
import MedicalProcedure from "./procedure";
import PlaceOfService from "./place-of-service";
import Drg from "./drg";
import TypeOfBill from "./type-of-bill";
import Revenue from "./revenue";
import Diagnosis from "./diagnosis";
import { useNavigate } from "react-router-dom";
import membersService from "@services/members.service";
import { dateFormatConfig, routeMap } from "@utils/constants";
import memberDetailsService from "@services/memberDetails.service";
import CustomTooltip from "@pages/tool-tip";
import { ColumnsType } from "antd/lib/table";
import { cloneDeep, filter, get, isEmpty, map } from "lodash";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
// import { formatDateStandard } from "@pages/utils/utils";
import { formatDisplayDate, getClientName, fixedDecimal, formatDownloadFileNameDate, centricClientCheck } from "@utils/helpers";
import { useGlobalInformationStore } from "@stores/global_informationStore";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { CSVLink } from "react-csv";
import DataNotFound, { TableLocale } from "@components/noDataFound";
import { NO_DATA_FOUND } from "../../../constants/constants";
import moment from "moment-timezone";
// import { Excel } from "antd-table-saveas-excel";

const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const { TabPane } = Tabs;

const MedicalHistory: FC<any> = ({ setActiveTabKey }) => {
  const [loadingForMedicalHistory, setMedicalHistoryFlag] = useState(false);
  const [medicalHistoryData, setMedicalHistoryData] = useState<any>({});
  const [reminderStartFilter, setReminderStartFilter] = useState();
  const [reminderEndFilter, setReminderEndFilter] = useState();
  const [medicationsTableData, setMedicationsTableData] = useState([]);
  // const [refresh, setRefresh] = useState(0);
  const [selectedTabKey, setSelectedTabKey] = useState<string>("1");
  const [activePanel, setActivePanel] = useState<any[]>([]);
  const navigate = useNavigate();
  const setAutoSroll = useGlobalInformationStore((state) => state.setAutoSroll);
  const isCentric = centricClientCheck();
  const excludedDataIndexes = isCentric ? ["mprRatio", "therapeuticClassCode"] : ["pharmacyName"];
  const includedDataIndexes = isCentric ? ["pharmacyName"] : [];

  const tableColumns: ColumnsType<any> | undefined = filter(
    [
      {
        title: "DATE",
        dataIndex: "date",
        width: "13%",
        ellipsis: {
          showTitle: false,
        },
        render: (date: any) => {
          const formattedDate = formatDisplayDate(date, "reminderDateActualFormat", "");
          return <CustomTooltip toolTipvalue={formattedDate} />;
        },
        sorter: (a: any, b: any) => {
          return a.date?.localeCompare(b?.date);
        },
      },
      {
        title: "NDC CODE",
        dataIndex: "ndcCode",
        width: isCentric ? "13%" : "12%",
        ellipsis: {
          showTitle: false,
        },
        render: (ndcCode: any) => <CustomTooltip toolTipvalue={ndcCode} />,
        sorter: (a: any, b: any) => {
          return a.ndcCode?.localeCompare(b?.ndcCode);
        },
      },
      {
        title: "COST",
        dataIndex: "cost",
        width: "13%",
        ellipsis: {
          showTitle: false,
        },
        align: centricClientCheck() ? "left" : "right",
        render: (cost: any, data: any) => {
          const children = (
            <div>
              <span style={{ fontWeight: "bold", marginRight: "8px" }}>$ </span>
              {`${fixedDecimal(cost, 2)} `}
            </div>
          );
          return <CustomTooltip toolTipvalue={children} />;
        },
        sorter: (a: any, b: any) => {
          return a.cost - b.cost;
        },
      },
      {
        title: "DRUG NAME",
        dataIndex: "drugName",
        ellipsis: {
          showTitle: false,
        },
        render: (drugName: any) => <CustomTooltip toolTipvalue={drugName} />,
        sorter: (a: any, b: any) => {
          return a.drugName?.localeCompare(b.drugName);
        },
      },
      {
        title: "THERAPEUTIC CLASS",
        dataIndex: "therapeuticClassCode",
        ellipsis: {
          showTitle: false,
        },
        render: (therapeuticClassCode: any) => <CustomTooltip toolTipvalue={therapeuticClassCode} />,
        sorter: (a: any, b: any) => {
          return a.therapeuticClassCode?.localeCompare(b.therapeuticClassCode);
        },
      },
      {
        title: "BRAND INFO",
        dataIndex: "brandInfo",
        width: isCentric ? "14%" : "13%",
        ellipsis: {
          showTitle: false,
        },
        render: (brandInfo: any) => <CustomTooltip toolTipvalue={brandInfo} />,
        sorter: (a: any, b: any) => {
          return a.brandInfo?.localeCompare(b?.brandInfo);
        },
      },
      {
        title: "MPR",
        dataIndex: "mprRatio",
        width: "12%",
        ellipsis: {
          showTitle: false,
        },
        render: (mprRatio: any) => <CustomTooltip toolTipvalue={mprRatio} />,
        sorter: (a: any, b: any) => {
          return a?.mprRatio?.split?.("%")?.[0] - b?.mprRatio?.split?.("%")?.[0];
        },
      },
      {
        title: "PHARMACY NAME",
        dataIndex: "pharmacyName",
        width: "18%",
        ellipsis: {
          showTitle: false,
        },
        render: (pharmacyName: any) => <CustomTooltip toolTipvalue={pharmacyName} />,
        sorter: (a: any, b: any) => {
          return a.pharmacyName?.localeCompare(b.pharmacyName);
        },
      },
    ],
    (col) => {
      return !excludedDataIndexes.includes(col.dataIndex) && (isCentric ? true : !includedDataIndexes.includes(col.dataIndex));
    }
  );

  const cMember = membersService?.getCurrentMember;
  useEffect(() => {
    console.log("MedicalHistory.useEffect1");
    appInsights.trackPageView({ name: "Medical History" });
  }, []);

  useEffect(() => {
    console.log("MedicalHistory.useEffect2");
    setActivePanel([]);
  }, []);

  useEffect(() => {
    console.log("MedicalHistory.useEffect3");

    const getMedicalHistory = (id: number, reminderStartFilter?: any, reminderEndFilter?: any) => {
      console.log("MedicalHistory.getMedicalHistory");
      setMedicalHistoryFlag(true);
      memberDetailsService.getMedicalHistoryData(id, reminderStartFilter, reminderEndFilter).subscribe({
        next: (v) => {
          console.log(v);
          setMedicalHistoryFlag(false);
          setMedicalHistoryData(v.data.data.message);
          setMedicationsTableData(v.data.data.message.medications);
          // setRefresh(refresh + 1);
        },
        error: (e) => {
          console.log(e);
          setMedicalHistoryFlag(false);
          setMedicalHistoryData({});
        },
      });
    };

    if (cMember && cMember?.patient_id) {
      let pId = parseInt(cMember?.patient_id);
      getMedicalHistory(pId, reminderStartFilter, reminderEndFilter);
    } else navigate(routeMap["members"]);
  }, [reminderStartFilter, reminderEndFilter, navigate, cMember?.patient_id]);

  const handleReminderDateFilter = (range: any) => {
    const reminderStartDate = range && range.length > 0 ? range[0].format("YYYY-MM-DD") : undefined;
    const reminderEndDate = range && range.length > 0 ? range[1].format("YYYY-MM-DD") : undefined;
    setReminderStartFilter(reminderStartDate);
    setReminderEndFilter(reminderEndDate);
  };

  /**
   * Column search functionality
   *
   */
  const [columnSearchInfo, setColumnSearchInfo] = useState({});

  const alternateMedicationNavigation = () => {
    setAutoSroll(true);
    setActiveTabKey("5");
  };

  const highlighterDiv = (text: any, searchWord: any, dataIndex: any) => {
    switch (dataIndex) {
      case "brandInfo":
        return text === "Brand" && getClientName() !== "Millennium" && getClientName() !== "Centric" ? (
          <span className="f16 fw500 blue-color cursor-type--pointer" onClick={alternateMedicationNavigation}>
            {text.toString()}
          </span>
        ) : (
          <Tooltip placement="topLeft" title={text ? text.toString() : ""}>
            <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ? text.toString() : ""} />
          </Tooltip>
        );
      default:
        return (
          <Tooltip placement="topLeft" title={text ? text.toString() : ""}>
            <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ? text.toString() : ""} />
          </Tooltip>
        );
    }
  };

  const onCollapseChange = () => {
    if (activePanel.length > 0) {
      setActivePanel([]);
    } else setActivePanel(["1"]);
  };

  const applyColumnSearch = (columnName: any, text: any) => {
    text = text ? text : get(columnSearchInfo, [columnName], "");
    if (text !== "") {
      let tempMedicationsData = medicalHistoryData?.medications.filter((data: any) => {
        if (data[columnName]) {
          return data[columnName].toString().toLowerCase().includes(text.toLowerCase());
        } else {
          return false;
        }
      });
      setMedicationsTableData(tempMedicationsData);
    } else {
      setMedicationsTableData(medicalHistoryData.medications);
    }
  };

  const inputref = useRef<any>(null);
  const _xyz: Array<{}> = map(cloneDeep(tableColumns), (dataInfo: any) => {
    return {
      ...dataInfo,

      filterDropdown: ({ confirm }: any) => (
        <div style={{ padding: "10px" }}>
          <Input
            ref={inputref}
            style={{ marginBottom: 8, display: "block" }}
            value={get(columnSearchInfo, dataInfo.dataIndex, "")}
            onChange={(e) => {
              setColumnSearchInfo({
                ...columnSearchInfo,
                [dataInfo.dataIndex]: e.target.value,
              });
            }}
            onPressEnter={(e: any): any => {
              applyColumnSearch(dataInfo.dataIndex, "");
              confirm({ closeDropdown: true });
              e.stopPropagation();
            }}
          />
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
              onClick={(e) => {
                setColumnSearchInfo({
                  [dataInfo.dataIndex]: get(columnSearchInfo, dataInfo.dataIndex, ""),
                });
                applyColumnSearch(dataInfo.dataIndex, "");
                confirm({ closeDropdown: true });
              }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                setColumnSearchInfo({ [dataInfo.dataIndex]: "" });
                applyColumnSearch("", "");
                confirm({ closeDropdown: true });
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon:
        dataInfo.dataIndex === "action"
          ? null
          : () => (
              <SearchOutlined
                style={{
                  color: get(columnSearchInfo, dataInfo.dataIndex, "") !== "" ? "#EF6D2A" : undefined,
                }}
              />
            ),
      render:
        dataInfo.dataIndex === "date"
          ? dataInfo.render
          : dataInfo.dataIndex === "cost"
          ? (text: any, record: any) => highlighterDiv("$ " + fixedDecimal(text, 2), get(columnSearchInfo, dataInfo.dataIndex, ""), dataInfo.dataIndex)
          : (text: any, record: any) => highlighterDiv(text, get(columnSearchInfo, dataInfo.dataIndex, ""), dataInfo.dataIndex),
      onFilter: (value: any, record: any) => (record[dataInfo.dataIndex] ? record[dataInfo.dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : ""),
      onFilterDropdownOpenChange: (visible: any) => {
        if (visible) {
          setTimeout(() => inputref.current?.select(), 100);
        }
      },
    };
  });

  // const hitDownloadAPI = (tabName: string) => {
  //   const getMedicalHistory = (id: number, reminderStartFilter?: any, reminderEndFilter?: any) => {
  //     console.log("MedicalHistory.getMedicalHistory");
  //     setMedicalHistoryFlag(true);
  //     memberDetailsService.getMedicalHistoryDataDownload({ patientId: id, fromDate: reminderStartFilter, toDate: reminderEndFilter, isDownload: true, tabName: tabName }).subscribe({
  //       next: (v) => {
  //         if (v.data.status === "OK") window.open(v.data.data.message, "_blank");
  //         setMedicalHistoryFlag(false);
  //       },
  //       error: (e) => {
  //         console.log(e);
  //         setMedicalHistoryFlag(false);
  //       },
  //     });
  //   };

  //   if (cMember && cMember?.patient_id) {
  //     let pId = parseInt(cMember?.patient_id);
  //     getMedicalHistory(pId, reminderStartFilter, reminderEndFilter);
  //   } else navigate(routeMap["members"]);
  // };

  const disabledDate = (current: any) => {
    const startOfMonth = moment().startOf("month");
    const monthsAgo = isCentric ? 11 : 11;
    const monthsRange = moment().subtract(monthsAgo, "months").startOf("month");
    return current && (current < monthsRange || current > startOfMonth.endOf("month"));
  };

  return (
    <Spin spinning={loadingForMedicalHistory} indicator={<HealthemSpinner size={70} />}>
      <div className="medical-history-wrapper">
        <Card
          style={{
            borderRadius: "10px",
            marginBottom: "10px",
            minHeight: "325px",
          }}
        >
          <Row style={{ justifyContent: "end" }}>
            <Col>
              {/* <div>
              <span>Latest Date: </span>
              <RangePicker picker="date" />
            </div> */}
              <div className="reminderDatePicker">
                <div className="reminderDateLabel">
                  <span className="icon-calendar f18" />
                  <span>Date of Service :</span>
                </div>
                <RangePicker
                  picker="date"
                  size="middle"
                  format={dateFormatConfig.get(getClientName())["defaultDateFormat"]}
                  allowClear={true}
                  dropdownClassName="reminder-picker"
                  // ranges={{
                  //   "More Filters:": [null, null],
                  //   "Next 7 Days": [moment(), moment().add(7, "days")],
                  //   "Last 7 Days": [moment().subtract(7, "days"), moment()],
                  // }}
                  onChange={(range) => {
                    handleReminderDateFilter(range);
                  }}
                  disabledDate={disabledDate}
                />
              </div>
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }}>
            <Tabs
              onChange={(activeKey) => {
                setSelectedTabKey(activeKey);
              }}
              activeKey={selectedTabKey}
              defaultActiveKey={"1"}
              type="card"
              className="medical-history-wrapper__medical-history-table-reports"
            >
              <TabPane tab="Medications" key="1">
                <Collapse
                  accordion={true}
                  activeKey={activePanel}
                  expandIconPosition="right"
                  bordered={false}
                  onChange={onCollapseChange}
                  style={{ border: "0.1rem solid #d9d9d9", marginBottom: "16px" }}
                >
                  <Panel
                    header={
                      <>
                        <span className="labelText">Cost Of Medication Distribution</span>
                        {/* <span className="icon icon-info" style={{ margin: "10px" }}></span> */}
                      </>
                    }
                    key="1"
                  >
                    <div style={{ margin: "24px 0" }}>
                      {loadingForMedicalHistory ? (
                        <></>
                      ) : isEmpty(medicalHistoryData?.medicationsGraph) ? (
                        <DataNotFound message={NO_DATA_FOUND} />
                      ) : (
                        <BarChart
                          data={
                            medicalHistoryData?.medicationsGraph && medicalHistoryData?.medicationsGraph.length > 0
                              ? medicalHistoryData?.medicationsGraph.sort((a: any, b: any) => {
                                  return b.cost - a.cost;
                                })
                              : []
                          }
                          xField="drugName"
                          yField="cost"
                        />
                      )}
                    </div>
                  </Panel>
                </Collapse>
                {/* <Row style={{ marginBottom: "16px" }}>
                  <Col span={24}>
                    <span className="labelText clr-n100">Medication Details</span>
                  </Col>
                </Row> */}
                <div className="ui-medical-history-tab-header">
                  <div className="ui-medical-history-tab-header__heading">Medication Details</div>
                  <div className="ui-medical-history-tab-header__download-icon">
                    <CSVLink
                      data={medicationsTableData}
                      headers={[
                        { label: "DATE", key: "date" },
                        { label: "NDC CODE", key: "ndcCode" },
                        { label: "COST", key: "cost" },
                        { label: "DRUG NAME", key: "drugName" },
                        { label: "THERAPEUTIC CLASS", key: "therapeuticClassCode" },
                        { label: "BRAND INFO", key: "brandInfo" },
                        { label: "MPR", key: "mprRatio" },
                        { label: "PHARMACY NAME", key: "pharmacyName" },
                      ].filter((header) => !(isCentric && ["mprRatio", "therapeuticClassCode"].includes(header.key)) && (isCentric || !["pharmacyName"].includes(header.key)))}
                      onClick={() => {
                        console.log("clicked");
                      }}
                      filename={`Medication Details_${formatDownloadFileNameDate(new Date())}`}
                    >
                      <Tooltip title="Download XLS" overlayClassName="download-tooltip">
                        <i className="btn-icon icon-download clr-n125" style={{ width: "20px", height: "42px" }} />
                      </Tooltip>
                    </CSVLink>
                  </div>
                </div>

                <Row>
                  <Col span={24}>
                    <Table
                      bordered={true}
                      pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ["5", "10", "15", "20", "25", "30"] }}
                      columns={_xyz}
                      dataSource={medicationsTableData}
                      className="table-rows"
                      locale={TableLocale}
                    />
                  </Col>
                </Row>
              </TabPane>

              <TabPane tab="Procedure" key="2">
                <div className="ui-medical-history-tab-header">
                  <div className="ui-medical-history-tab-header__heading">Procedure</div>
                  <div className="ui-medical-history-tab-header__download-icon">
                    <CSVLink
                      data={medicalHistoryData?.procedure}
                      headers={[
                        { label: "DATE", key: "date" },
                        { label: "PROCEDURE CODE", key: "procedureCode" },
                        { label: "COST", key: "cost" },
                        { label: "DESCRIPTION", key: "procedureDesc" },
                      ]}
                      onClick={() => {
                        console.log("clicked");
                      }}
                      filename={`Procedure_${formatDownloadFileNameDate(new Date())}`}
                    >
                      <Tooltip title="Download XLS" overlayClassName="download-tooltip">
                        <i className="btn-icon icon-download clr-n125" style={{ width: "20px", height: "42px" }} />
                      </Tooltip>
                    </CSVLink>
                  </div>
                </div>
                <MedicalProcedure data={medicalHistoryData?.procedure} />
              </TabPane>

              <TabPane tab="Diagnosis" key="3">
                <div className="ui-medical-history-tab-header">
                  <div className="ui-medical-history-tab-header__heading">Diagnosis</div>
                  <div className="ui-medical-history-tab-header__download-icon">
                    <CSVLink
                      data={medicalHistoryData?.diagnosis}
                      headers={[
                        { label: "DATE", key: "date" },
                        { label: "DIAGNOSIS CODE", key: "diagnosisCode" },
                        { label: "COST", key: "cost" },
                        { label: "DESCRIPTION", key: "diagnosisDescription" },
                        { label: "CHRONIC CONDITION", key: "chronic" },
                      ]}
                      onClick={() => {
                        console.log("clicked");
                      }}
                      filename={`Diagnosis_${formatDownloadFileNameDate(new Date())}`}
                    >
                      <Tooltip title="Download XLS" overlayClassName="download-tooltip">
                        <i className="btn-icon icon-download clr-n125" style={{ width: "20px", height: "42px" }} />
                      </Tooltip>
                    </CSVLink>
                  </div>
                </div>
                <Diagnosis data={medicalHistoryData?.diagnosis} />
              </TabPane>

              <TabPane tab="Place of Service" key="4">
                <Collapse
                  accordion={true}
                  activeKey={activePanel}
                  expandIconPosition="right"
                  bordered={false}
                  onChange={onCollapseChange}
                  style={{ border: "0.1rem solid #d9d9d9", marginBottom: "16px" }}
                >
                  <Panel
                    header={
                      <>
                        <span className="labelText">Place Of Service Distribution</span>
                        {/* <span className="icon icon-info" style={{ margin: "10px" }}></span> */}
                      </>
                    }
                    key="1"
                  >
                    <div style={{ margin: "24px 0" }}>
                      {loadingForMedicalHistory ? (
                        <></>
                      ) : isEmpty(medicalHistoryData?.placeOfServiceGraph) ? (
                        <DataNotFound message={NO_DATA_FOUND} />
                      ) : (
                        <BarChart
                          data={
                            medicalHistoryData?.placeOfServiceGraph && medicalHistoryData?.placeOfServiceGraph.length > 0
                              ? medicalHistoryData?.placeOfServiceGraph.sort((a: any, b: any) => {
                                  return b.cost - a.cost;
                                })
                              : []
                          }
                          xField="placeOfServiceDesc"
                          yField="cost"
                        />
                      )}
                    </div>
                  </Panel>
                </Collapse>
                <div className="ui-medical-history-tab-header">
                  <div className="ui-medical-history-tab-header__heading">Place of Service</div>
                  <div className="ui-medical-history-tab-header__download-icon">
                    <CSVLink
                      data={medicalHistoryData?.placeOfService}
                      headers={[
                        { label: "DATE", key: "date" },
                        { label: "POS CODE", key: "posCode" },
                        ...(isCentric
                          ? [{ label: "CLAIM SERVICE TYPE", key: "claimServiceType" }]
                          : [] ),
                        { label: "COST", key: "cost" },
                        { label: "PLACE OF SERVICE NAME", key: "placeOfServiceDesc" },
                      ]}
                      onClick={() => {
                        console.log("clicked");
                      }}
                      filename={`Place of Service_${formatDownloadFileNameDate(new Date())}`}
                    >
                      <Tooltip title="Download XLS" overlayClassName="download-tooltip">
                        <i className="btn-icon icon-download clr-n125" style={{ width: "20px", height: "42px" }} />
                      </Tooltip>
                    </CSVLink>
                  </div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <PlaceOfService data={medicalHistoryData?.placeOfService} />
                </div>
              </TabPane>
              <TabPane tab="Revenue Code" key="5">
                <div className="ui-medical-history-tab-header">
                  <div className="ui-medical-history-tab-header__heading">Revenue Code</div>
                  <div className="ui-medical-history-tab-header__download-icon">
                    <CSVLink
                      data={medicalHistoryData?.revenue}
                      headers={[
                        { label: "DATE", key: "date" },
                        { label: "REVENUE CODE", key: "revenueCode" },
                        { label: "COST", key: "cost" },
                        { label: "DESCRIPTION", key: "revenueDesc" },
                      ]}
                      onClick={() => {
                        console.log("clicked");
                      }}
                      filename={`Revenue_${formatDownloadFileNameDate(new Date())}`}
                    >
                      <Tooltip title="Download XLS" overlayClassName="download-tooltip">
                        <i className="btn-icon icon-download clr-n125" style={{ width: "20px", height: "42px" }} />
                      </Tooltip>
                    </CSVLink>
                  </div>
                </div>
                <Revenue data={medicalHistoryData?.revenue} />
              </TabPane>

              <TabPane tab="Type of Bill" key="6">
                <div className="ui-medical-history-tab-header">
                  <div className="ui-medical-history-tab-header__heading">Type Of Bill</div>
                  <div className="ui-medical-history-tab-header__download-icon">
                    <CSVLink
                      data={medicalHistoryData?.typeOfBill}
                      headers={[
                        { label: "DATE", key: "date" },
                        { label: "BILL TYPE CODE", key: "billTypeCode" },
                        { label: "COST", key: "cost" },
                        { label: "DESCRIPTION", key: "billTypeDesc" },
                      ]}
                      onClick={() => {
                        console.log("clicked");
                      }}
                      filename={`Type of Bill_${formatDownloadFileNameDate(new Date())}`}
                    >
                      <Tooltip title="Download XLS" overlayClassName="download-tooltip">
                        <i className="btn-icon icon-download clr-n125" style={{ width: "20px", height: "42px" }} />
                      </Tooltip>
                    </CSVLink>
                  </div>
                </div>
                <TypeOfBill data={medicalHistoryData?.typeOfBill} />
              </TabPane>

              <TabPane tab={isCentric ? "Institutional" : "DRG"} key="7">
                <div className="ui-medical-history-tab-header">
                  <div className="ui-medical-history-tab-header__heading">{isCentric ? "Institutional" : "DRG"}</div>
                  <div className="ui-medical-history-tab-header__download-icon">
                    <CSVLink
                      data={medicalHistoryData?.drg}
                      headers={filter([
                        { label: "ADMISSION DATE", key: "date" },
                        { label: "DISCHARGE DATE", key: "hospital_admission_end_date" },
                        { label: "DATE", key: "date" },
                        { label: "CLAIM SERVICE TYPE", key: "claimServiceType" },
                        { label: "DRG CODE", key: "drgCode" },
                        { label: "COST", key: "cost" },
                        { label: "DESCRIPTION", key: "drgDesc" },
                      ], (item) => {
                        if (isCentric) {
                          return item.label !== "DATE";
                        } else {
                          return item.label !== "ADMISSION DATE" && item.key !== "hospital_admission_end_date" && item.key !== "claimServiceType";
                        } 
                      })}
                      onClick={() => {
                        console.log("clicked");
                      }}
                      filename={`DRG_${formatDownloadFileNameDate(new Date())}`}
                    >
                      <Tooltip title="Download XLS" overlayClassName="download-tooltip">
                        <i className="btn-icon icon-download clr-n125" style={{ width: "20px", height: "42px" }} />
                      </Tooltip>
                    </CSVLink>
                  </div>
                </div>
                <Drg data={medicalHistoryData?.drg} />
              </TabPane>
            </Tabs>
          </Row>
        </Card>
      </div>
    </Spin>
  );
};

export default withAITracking(reactPlugin, MedicalHistory);
